import { useState, useCallback, useEffect } from "react";
import type React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Camera, Save, SkipForward, Loader2, ChevronLeft, ChevronRight, RefreshCw, ExternalLink, Edit2, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface WhatcomCountyTextParserProps {
  onRecordsSaved?: () => void;
}

interface ExtractedRecord {
  document_number: string | null;
  document_type: string | null;
  filing_date: string | null;
  grantor_name: string | null;
  grantor_address: string | null;
  grantee_name: string | null;
  grantee_address: string | null;
  property_address: string | null;
  legal_description: string | null;
  loan_amount: number | null;
  interest_rate: number | null;
  monthly_payment: number | null;
  term_months: number | null;
  balloon_amount: number | null;
  balloon_due_date: string | null;
  parcel_id: string | null;
  trustee_name: string | null;
}

interface ReviewRecord {
  id: string;
  document_number: string;
  filing_date: string;
  grantor_name: string;
  grantee_name: string;
  grantor_address: string | null;
  document_type: string;
  legal_description: string | null;
}

export const WhatcomCountyTextParser = ({ onRecordsSaved }: WhatcomCountyTextParserProps) => {
  // Screenshot capture state
  const [imageData, setImageData] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedRecord | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);

  // Review mode state
  const [reviewRecords, setReviewRecords] = useState<ReviewRecord[]>([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [isLoadingReview, setIsLoadingReview] = useState(false);
  const [reviewImageData, setReviewImageData] = useState<string | null>(null);
  const [reviewExtractedData, setReviewExtractedData] = useState<ExtractedRecord | null>(null);
  const [reviewSavedCount, setReviewSavedCount] = useState(0);
  const [reviewSkippedCount, setReviewSkippedCount] = useState(0);

  // Paste target routing
  const [pasteTarget, setPasteTarget] = useState<'review' | 'screenshot'>('screenshot');

  const extractImageDataUrlFromClipboard = useCallback(async (clipboardData?: DataTransfer | null) => {
    const items = clipboardData?.items;
    if (!items) return null;

    for (const item of items) {
      if (item.type?.startsWith('image/')) {
        const file = item.getAsFile();
        if (!file) continue;

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        return base64;
      }
    }
    return null;
  }, []);

  const applyPastedImage = useCallback(
    async ({
      clipboardData,
      target,
    }: {
      clipboardData: DataTransfer | null | undefined;
      target: 'review' | 'screenshot';
    }) => {
      const base64 = await extractImageDataUrlFromClipboard(clipboardData ?? null);
      if (!base64) return false;

      if (target === 'review') {
        setReviewImageData(base64);
        setReviewExtractedData(null);
        toast.success('Screenshot captured! Click "Extract" to process.');
      } else {
        setImageData(base64);
        setExtractedData(null);
        toast.success('Screenshot captured! Click "Extract Data" to process.');
      }
      return true;
    },
    [extractImageDataUrlFromClipboard]
  );

  const handleScreenshotPaste = useCallback(
    async (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setPasteTarget('screenshot');

      const ok = await applyPastedImage({ clipboardData: e.clipboardData, target: 'screenshot' });
      if (!ok) toast.error('No image found in clipboard');
    },
    [applyPastedImage]
  );

  const handleReviewPaste = useCallback(
    async (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setPasteTarget('review');

      const ok = await applyPastedImage({ clipboardData: e.clipboardData, target: 'review' });
      if (!ok) toast.error('No image found in clipboard');
    },
    [applyPastedImage]
  );

  const pasteScreenshotFromClipboard = useCallback(async (target: 'review' | 'screenshot') => {
    try {
      setPasteTarget(target);

      const items = await (navigator.clipboard as any)?.read?.();
      if (!items || items.length === 0) {
        toast.error('Clipboard is empty or blocked by the browser');
        return;
      }

      for (const item of items) {
        const type = item.types?.find((t: string) => t.startsWith('image/'));
        if (!type) continue;

        const blob: Blob = await item.getType(type);
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        if (target === 'review') {
          setReviewImageData(base64);
          setReviewExtractedData(null);
          toast.success('Screenshot captured! Click "Extract" to process.');
        } else {
          setImageData(base64);
          setExtractedData(null);
          toast.success('Screenshot captured! Click "Extract Data" to process.');
        }
        return;
      }

      toast.error('No image found in clipboard');
    } catch (err: any) {
      toast.error(err?.message || 'Clipboard access blocked by the browser');
    }
  }, []);

  useEffect(() => {
    const onWindowPaste = async (e: ClipboardEvent) => {
      await applyPastedImage({ clipboardData: e.clipboardData, target: pasteTarget });
    };

    window.addEventListener('paste', onWindowPaste);
    return () => window.removeEventListener('paste', onWindowPaste);
  }, [applyPastedImage, pasteTarget]);

  const loadRecordsForReview = async () => {
    setIsLoadingReview(true);
    try {
      const { data, error } = await supabase
        .from('county_research')
        .select('id, document_number, filing_date, grantor_name, grantee_name, grantee_address, grantor_address, document_type, legal_description')
        .eq('county', 'whatcom')
        .is('grantee_address', null) // DOT: lender address is in grantee_address
        .is('deleted_at', null)
        .order('filing_date', { ascending: false })
        .limit(100);

      if (error) throw error;

      setReviewRecords(data || []);
      setReviewIndex(0);
      setReviewImageData(null);
      setReviewExtractedData(null);
      toast.success(`Loaded ${data?.length || 0} records needing address review`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load records');
    } finally {
      setIsLoadingReview(false);
    }
  };

  const handleExtractData = async () => {
    if (!imageData) {
      toast.error("Please paste a screenshot first");
      return;
    }

    setIsProcessing(true);
    setExtractedData(null);

    try {
      const { data, error } = await supabase.functions.invoke("extract-whatcom-screenshot", {
        body: { imageData, county: 'whatcom' }
      });

      if (error) throw error;

      if (data.success) {
        setExtractedData(data.extractedData);
        toast.success("Data extracted! Review and save when ready.");
      } else {
        throw new Error(data.error || "Extraction failed");
      }
    } catch (error: any) {
      console.error("Extract error:", error);
      toast.error(error.message || "Failed to extract data");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveRecord = async () => {
    if (!extractedData) {
      toast.error("No extracted data to save");
      return;
    }

    setIsProcessing(true);
    try {
      // In WA Deeds of Trust: Grantor=Borrower, Beneficiary=Lender/Seller
      // Store correctly: grantor=borrower, grantee=lender (DOT standard)
      const recordToInsert = {
        county: 'whatcom',
        document_number: extractedData.document_number || `WHATCOM-${Date.now()}`,
        document_type: extractedData.document_type || 'DEED OF TRUST',
        filing_date: extractedData.filing_date || new Date().toISOString().split('T')[0],
        grantor_name: extractedData.grantor_name,      // Borrower stays as grantor
        grantor_address: extractedData.grantor_address, // Borrower address
        grantee_name: extractedData.grantee_name,      // Lender/Beneficiary stays as grantee
        grantee_address: extractedData.grantee_address, // Lender/Beneficiary address (the golden field)
        property_address: extractedData.property_address,
        legal_description: extractedData.legal_description,
        loan_amount: extractedData.loan_amount,
        interest_rate: extractedData.interest_rate,
        monthly_payment: extractedData.monthly_payment,
        term_months: extractedData.term_months,
        balloon_amount: extractedData.balloon_amount,
        balloon_due_date: extractedData.balloon_due_date,
        is_potential_owner_finance: true,
        confidence_score: 85,
        address_extraction_method: 'ai_screenshot',
        status: 'new'
      };

      const { error } = await supabase
        .from('county_research')
        .insert([recordToInsert]);

      if (error) throw error;

      backupRecordsToExcel([recordToInsert], 'whatcom', 'whatcom_parser');

      toast.success("Record saved successfully!");
      setSavedCount(prev => prev + 1);
      setImageData(null);
      setExtractedData(null);
      onRecordsSaved?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to save record");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkip = () => {
    setSkippedCount(prev => prev + 1);
    setImageData(null);
    setExtractedData(null);
    toast.info("Skipped - paste next screenshot");
  };

  // Review mode handlers
  const handleReviewExtract = async () => {
    if (!reviewImageData || reviewRecords.length === 0) {
      toast.error("Paste a screenshot first");
      return;
    }

    setIsProcessing(true);
    setReviewExtractedData(null);

    try {
      const currentRecord = reviewRecords[reviewIndex];

      const { data, error } = await supabase.functions.invoke("extract-whatcom-screenshot", {
        body: { 
          imageData: reviewImageData, 
          county: 'whatcom',
          recordId: currentRecord.id
        }
      });

      if (error) throw error;

      if (data.success) {
        setReviewExtractedData(data.extractedData);
        toast.success("Address extracted! Edit if needed, then save.");
      } else {
        throw new Error(data.error || "Extraction failed");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to extract");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReviewSave = async () => {
    if (!reviewExtractedData || reviewRecords.length === 0) return;

    setIsProcessing(true);
    try {
      const currentRecord = reviewRecords[reviewIndex];
      
      // DOT standard: grantee=lender/beneficiary (the address we want), grantor=borrower
      const { error } = await supabase
        .from('county_research')
        .update({
          grantee_address: reviewExtractedData.grantee_address, // Lender/Beneficiary address
          grantor_address: reviewExtractedData.grantor_address,  // Borrower address
          loan_amount: reviewExtractedData.loan_amount,
          interest_rate: reviewExtractedData.interest_rate,
          monthly_payment: reviewExtractedData.monthly_payment,
          address_extraction_method: 'ai_screenshot',
          address_needs_review: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentRecord.id);

      if (error) throw error;

      toast.success("Record updated!");
      setReviewSavedCount(prev => prev + 1);
      
      // Move to next record
      if (reviewIndex < reviewRecords.length - 1) {
        setReviewIndex(prev => prev + 1);
        setReviewImageData(null);
        setReviewExtractedData(null);
      } else {
        toast.info("All records reviewed!");
      }
      
      onRecordsSaved?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReviewSkip = () => {
    setReviewSkippedCount(prev => prev + 1);
    if (reviewIndex < reviewRecords.length - 1) {
      setReviewIndex(prev => prev + 1);
      setReviewImageData(null);
      setReviewExtractedData(null);
    }
  };

  // Keyboard shortcuts for review mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (reviewRecords.length === 0) return;
      
      if (e.key === 'ArrowLeft' && reviewIndex > 0) {
        setReviewIndex(prev => prev - 1);
        setReviewImageData(null);
        setReviewExtractedData(null);
      } else if (e.key === 'ArrowRight' && reviewIndex < reviewRecords.length - 1) {
        setReviewIndex(prev => prev + 1);
        setReviewImageData(null);
        setReviewExtractedData(null);
      } else if (e.key === 's' && reviewExtractedData) {
        e.preventDefault();
        handleReviewSave();
      } else if (e.key === 'k') {
        handleReviewSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [reviewIndex, reviewRecords.length, reviewExtractedData]);

  const currentReviewRecord = reviewRecords[reviewIndex];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Whatcom County, WA - Screenshot Research Bot
          </CardTitle>
          <CardDescription>
            Paste screenshots of Deed of Trust documents to extract beneficiary/seller addresses and loan terms. 
            Optimized for split-screen workflow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats */}
          <div className="flex gap-4 text-sm">
            <Badge variant="outline" className="bg-primary/10">
              <CheckCircle className="h-3 w-3 mr-1" /> Saved: {savedCount}
            </Badge>
            <Badge variant="outline">Skipped: {skippedCount}</Badge>
          </div>

          {/* Paste Zone */}
          <div
            className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            tabIndex={0}
            contentEditable
            suppressContentEditableWarning
            onPaste={handleScreenshotPaste}
            onClick={(e) => {
              setPasteTarget('screenshot');
              (e.currentTarget as HTMLDivElement).focus();
            }}
            onFocus={() => setPasteTarget('screenshot')}
          >
            {!imageData ? (
              <div className="space-y-2">
                <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="text-muted-foreground">
                  Click here and press <kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl+V</kbd> to paste a screenshot
                </p>
                <div className="flex items-center justify-center gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      pasteScreenshotFromClipboard('screenshot');
                    }}
                  >
                    Paste from Clipboard
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <img
                  src={imageData}
                  alt="Pasted screenshot"
                  className="max-h-64 mx-auto rounded border"
                />
                <div className="flex gap-2 justify-center">
                  <Button onClick={handleExtractData} disabled={isProcessing}>
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Extracting...
                      </>
                    ) : (
                      "Extract Data"
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setImageData(null)}>
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Extracted Data - Editable */}
          {extractedData && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-4 space-y-4">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  Extracted Data <Edit2 className="h-3 w-3 text-muted-foreground" />
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Document #</Label>
                    <Input
                      value={extractedData.document_number || ''}
                      onChange={(e) => setExtractedData({...extractedData, document_number: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Filing Date</Label>
                    <Input
                      value={extractedData.filing_date || ''}
                      onChange={(e) => setExtractedData({...extractedData, filing_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Beneficiary/Seller Address (editable)</Label>
                  <Input
                    value={extractedData.grantee_address || ''}
                    onChange={(e) => setExtractedData({...extractedData, grantee_address: e.target.value})}
                    placeholder="Enter or correct beneficiary address"
                    className="mt-1 font-medium"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Grantor/Borrower</Label>
                    <Input
                      value={extractedData.grantor_name || ''}
                      onChange={(e) => setExtractedData({...extractedData, grantor_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Grantee/Beneficiary</Label>
                    <Input
                      value={extractedData.grantee_name || ''}
                      onChange={(e) => setExtractedData({...extractedData, grantee_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                      Loan Amount
                      {!extractedData.loan_amount && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-destructive/10 text-destructive border-destructive/30">
                          Not captured
                        </Badge>
                      )}
                      {extractedData.loan_amount && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-primary/10 text-primary border-primary/30">
                          Captured
                        </Badge>
                      )}
                    </Label>
                    <Input
                      type="number"
                      value={extractedData.loan_amount || ''}
                      onChange={(e) => setExtractedData({...extractedData, loan_amount: parseFloat(e.target.value) || null})}
                      placeholder="Enter loan amount"
                      className={`mt-1 ${!extractedData.loan_amount ? 'border-destructive/50 bg-destructive/5 focus:border-destructive' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Interest Rate %</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={extractedData.interest_rate || ''}
                      onChange={(e) => setExtractedData({...extractedData, interest_rate: parseFloat(e.target.value) || null})}
                      placeholder="e.g. 6.5"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Monthly Payment</Label>
                    <Input
                      type="number"
                      value={extractedData.monthly_payment || ''}
                      onChange={(e) => setExtractedData({...extractedData, monthly_payment: parseFloat(e.target.value) || null})}
                      placeholder="e.g. 850"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Legal Description / Parcel ID</Label>
                  <Input
                    value={extractedData.legal_description || ''}
                    onChange={(e) => setExtractedData({...extractedData, legal_description: e.target.value})}
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSaveRecord} disabled={isProcessing} className="flex-1">
                    <Save className="mr-2 h-4 w-4" />
                    Save Record
                  </Button>
                  <Button variant="outline" onClick={handleSkip}>
                    <SkipForward className="mr-2 h-4 w-4" />
                    Skip
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Review Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Review Mode - Update Missing Addresses
            </span>
            <Button onClick={loadRecordsForReview} disabled={isLoadingReview} size="sm">
              {isLoadingReview ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load Records'}
            </Button>
          </CardTitle>
          <CardDescription>
            Review existing records missing beneficiary addresses. Use keyboard: ← → to navigate, S to save, K to skip.
          </CardDescription>
        </CardHeader>
        
        {reviewRecords.length > 0 && (
          <CardContent className="space-y-4">
            {/* Progress & Stats */}
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Badge variant="outline" className="bg-primary/10">Saved: {reviewSavedCount}</Badge>
                <Badge variant="outline">Skipped: {reviewSkippedCount}</Badge>
              </div>
              <span className="text-sm text-muted-foreground">
                Record {reviewIndex + 1} of {reviewRecords.length}
              </span>
            </div>

            {/* Navigation */}
            <div className="flex gap-2 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setReviewIndex(prev => prev - 1);
                  setReviewImageData(null);
                  setReviewExtractedData(null);
                }}
                disabled={reviewIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setReviewIndex(prev => prev + 1);
                  setReviewImageData(null);
                  setReviewExtractedData(null);
                }}
                disabled={reviewIndex >= reviewRecords.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Current Record Info */}
            {currentReviewRecord && (
              <Card className="bg-muted/30">
                <CardContent className="pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">Doc #: {currentReviewRecord.document_number}</span>
                    <span>{currentReviewRecord.filing_date}</span>
                  </div>
                  <div><strong>Grantor:</strong> {currentReviewRecord.grantor_name}</div>
                  <div><strong>Grantee:</strong> {currentReviewRecord.grantee_name}</div>
                  <div><strong>Type:</strong> {currentReviewRecord.document_type}</div>
                  {currentReviewRecord.legal_description && (
                    <div className="text-xs text-muted-foreground truncate">
                      {currentReviewRecord.legal_description}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Paste Zone for Review */}
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer transition-colors hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              tabIndex={0}
              contentEditable
              suppressContentEditableWarning
              onPaste={handleReviewPaste}
              onClick={(e) => {
                setPasteTarget('review');
                (e.currentTarget as HTMLDivElement).focus();
              }}
              onFocus={() => setPasteTarget('review')}
            >
              {!reviewImageData ? (
                <div className="space-y-1">
                  <Camera className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click and paste screenshot (Ctrl+V)
                  </p>
                  <div className="pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        pasteScreenshotFromClipboard('review');
                      }}
                    >
                      Paste from Clipboard
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <img
                    src={reviewImageData}
                    alt="Review screenshot"
                    className="max-h-48 mx-auto rounded border"
                  />
                  <Button onClick={handleReviewExtract} disabled={isProcessing} size="sm">
                    {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Extract Address
                  </Button>
                </div>
              )}
            </div>

            {/* Extracted Data in Review Mode - Editable */}
            {reviewExtractedData && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-4 space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    Extracted Data <Edit2 className="h-3 w-3 text-muted-foreground" />
                  </h4>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Beneficiary/Seller Address (editable)</Label>
                    <Input
                      value={reviewExtractedData.grantee_address || ''}
                      onChange={(e) => setReviewExtractedData({
                        ...reviewExtractedData,
                        grantee_address: e.target.value
                      })}
                      placeholder="Enter or correct beneficiary address"
                      className="mt-1 font-medium"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        Loan Amount
                        {!reviewExtractedData.loan_amount && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-destructive/10 text-destructive border-destructive/30">
                            Not captured
                          </Badge>
                        )}
                        {reviewExtractedData.loan_amount && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-primary/10 text-primary border-primary/30">
                            Captured
                          </Badge>
                        )}
                      </Label>
                      <Input
                        type="number"
                        value={reviewExtractedData.loan_amount || ''}
                        onChange={(e) => setReviewExtractedData({
                          ...reviewExtractedData,
                          loan_amount: parseFloat(e.target.value) || null
                        })}
                        placeholder="Enter loan amount"
                        className={`mt-1 ${!reviewExtractedData.loan_amount ? 'border-destructive/50 bg-destructive/5 focus:border-destructive' : ''}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Interest %</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={reviewExtractedData.interest_rate || ''}
                        onChange={(e) => setReviewExtractedData({
                          ...reviewExtractedData,
                          interest_rate: parseFloat(e.target.value) || null
                        })}
                        placeholder="e.g. 6.5"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Monthly Pmt</Label>
                      <Input
                        type="number"
                        value={reviewExtractedData.monthly_payment || ''}
                        onChange={(e) => setReviewExtractedData({
                          ...reviewExtractedData,
                          monthly_payment: parseFloat(e.target.value) || null
                        })}
                        placeholder="e.g. 850"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleReviewSave} disabled={isProcessing} className="flex-1">
                      <Save className="mr-2 h-4 w-4" />
                      Save & Next (S)
                    </Button>
                    <Button variant="outline" onClick={handleReviewSkip}>
                      <SkipForward className="mr-2 h-4 w-4" />
                      Skip (K)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        )}
      </Card>

      {/* Instructions */}
      <div className="text-xs text-muted-foreground bg-muted/30 p-4 rounded-lg">
        <p className="font-medium mb-2">Whatcom County Workflow:</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Open Whatcom County recorder website in a split-screen</li>
          <li>Search for Deed of Trust documents</li>
          <li>Take screenshot of document (Win+Shift+S or Cmd+Shift+4)</li>
          <li>Click paste zone above and press Ctrl+V</li>
          <li>Click "Extract Data" to pull beneficiary address and loan terms</li>
          <li>Edit any fields if needed (fix misspellings)</li>
          <li>Click "Save Record" to store in database</li>
        </ol>
        <p className="mt-2">
          <strong>Key fields to capture:</strong> Beneficiary address (seller/lender), loan amount, interest rate, 
          grantor (borrower), grantee (beneficiary), parcel ID
        </p>
      </div>
    </div>
  );
};
